import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { api } from '../api/client';
import { detectOS } from '../utils/detectOS';

const DOC_BASE = (process.env.REACT_APP_API_BASE || '/api') + '/public/documents';

export default function DocumentPreviewModal({ docType, onClose, requireAcceptance = false, onAccept }) {
  const { t, language } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMsg, setErrorMsg] = useState('');
  const [objectUrl, setObjectUrl] = useState(null);
  // Finché non arriva la configurazione assumiamo l'anteprima attiva, così
  // non si vede un lampo di layout diverso appena il popup si apre.
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const [autoOpenSucceeded, setAutoOpenSucceeded] = useState(false);
  const autoHandledRef = useRef(false);

  const docLabel = docType === 'security' ? t('docSecurityTitle') : t('docPrivacyTitle');

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;

    async function load() {
      setStatus('loading');
      try {
        const res = await fetch(`${DOC_BASE}/${docType}?lang=${language}`, { credentials: 'include' });
        if (!res.ok) {
          let message = t('docGenericError');
          try {
            const data = await res.json();
            if (data?.error) message = data.error;
          } catch {
            // risposta non in formato JSON, teniamo il messaggio generico
          }
          if (!cancelled) {
            setErrorMsg(message);
            setStatus('error');
          }
          return;
        }
        const blob = await res.blob();
        createdUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setObjectUrl(createdUrl);
          setStatus('ready');
        }
      } catch {
        if (!cancelled) {
          setErrorMsg(t('docConnError'));
          setStatus('error');
        }
      }
    }

    load();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, language]);

  // Legge la configurazione (per sistema operativo) di anteprima PDF nell'app,
  // impostata dall'amministratore: su alcuni dispositivi (tipicamente Android)
  // l'anteprima incorporata non si vede bene ed è meglio disattivarla.
  useEffect(() => {
    let cancelled = false;
    api.getDocPreviewConfig()
      .then((res) => {
        if (cancelled) return;
        const os = detectOS();
        setPreviewEnabled(Boolean(res?.data?.[os]));
      })
      .catch(() => {
        if (!cancelled) setPreviewEnabled(true);
      });
    return () => { cancelled = true; };
  }, []);

  const openDocument = () => {
    const win = window.open(objectUrl, '_blank', 'noopener,noreferrer');
    if (win) setAutoOpenSucceeded(true);
    return win;
  };

  // Quando l'anteprima è disattivata: apre subito il documento, lo segna
  // come letto, e resta in ascolto di quando l'utente torna su questa
  // pagina (chiudendo la scheda del PDF o l'app esterna che l'ha aperto)
  // per chiudere anche questo popup — niente da cliccare in mezzo.
  useEffect(() => {
    if (status !== 'ready' || previewEnabled || autoHandledRef.current) return undefined;
    autoHandledRef.current = true;

    if (requireAcceptance) onAccept?.();
    openDocument();

    const handleReturn = () => {
      if (document.visibilityState === 'visible') onClose();
    };
    document.addEventListener('visibilitychange', handleReturn);
    window.addEventListener('focus', handleReturn);

    return () => {
      document.removeEventListener('visibilitychange', handleReturn);
      window.removeEventListener('focus', handleReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, previewEnabled]);

  return createPortal(
    <div className="modal-overlay" onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="modal-card modal-card--wide pdf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pdf-modal-header">
          <h2>{docLabel}</h2>
          <button type="button" className="pdf-modal-close" onClick={onClose} aria-label={t('close')}>✕</button>
        </div>

        {status === 'loading' && <p className="modal-subtitle">{t('docLoading')}</p>}
        {status === 'error' && <div className="form-error">{errorMsg}</div>}

        {status === 'ready' && previewEnabled && (
          <>
            <iframe title={docLabel} src={objectUrl} className="pdf-modal-frame" />
            {requireAcceptance && (
              <div className="btn-row" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-ghost" onClick={onClose}>{t('close')}</button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => { onAccept?.(); onClose(); }}
                >
                  {t('docAcceptButton')}
                </button>
              </div>
            )}
          </>
        )}

        {status === 'ready' && !previewEnabled && (
          <div style={{ textAlign: 'center', padding: '24px 0' }}>
            <p className="modal-subtitle">{t('docAutoOpenNote')}</p>
            {!autoOpenSucceeded && (
              <button type="button" className="btn btn-primary" onClick={openDocument} style={{ width: 'auto', padding: '12px 28px' }}>
                {t('docOpenNewTab')}
              </button>
            )}
            <button type="button" className="btn btn-ghost" onClick={onClose} style={{ marginTop: 14 }}>
              {t('close')}
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
